import React, { useEffect, useState } from "react"
import { SerializedStyles } from "@emotion/react"
import {
	AnimatedEntrance,
	Box,
	BoxProps,
	Button,
	IconButton,
	nextThread,
	NumericInputEcomm,
	Paragraph,
	SelectEcomm,
	usePrevious,
} from "@ncs/web-legos"
import { UnitMapping } from "./UnitMapping"
import {
	box,
	clearText,
	editBox,
	inputStyle,
	quantityControlLabel,
	quantityLabel,
	saveIcon,
	trashCan,
	unitStyle,
} from "./styles"

interface SelectOption {
	value: string
	text: string
}

type SelectChangeValue = string | null | undefined
type NumericChangeValue = number | undefined
type QuantityMode = "select" | "input"
type QuantityKeyboardEvent = React.KeyboardEvent<HTMLInputElement | HTMLSelectElement>

const options: SelectOption[] = Array.from({ length: 31 }, (_, i) => ({
	value: String(i),
	text: String(i),
})).concat([{ value: "30+", text: "30+" }])

interface QuantityControlProps extends Omit<BoxProps, "display" | "onChange"> {
	unitOfMeasure?: string | null | undefined
	value?: number
	onChange: (newValue: number) => void
	onChooseZero?: () => void
	useUpdateButton?: boolean
	iconCss?: SerializedStyles
	labelPosition?: "top" | "left"
	unitAtTop?: boolean
	selectCss?: SerializedStyles
	clearVariant?: "icon" | "text"
}

export const QuantityControl: React.FC<QuantityControlProps> = ({
	value: propValue,
	onChange,
	onChooseZero,
	iconCss,
	useUpdateButton = false,
	unitOfMeasure = null,
	unitAtTop = false,
	selectCss,
	clearVariant = "icon",
}) => {
	const [mode, setMode] = useState<QuantityMode>(
		propValue && propValue > 30 ? "input" : "select"
	)
	const [localValue, setLocalValue] = useState<number | undefined>(propValue)
	const [showUpdateButton, setShowUpdateButton] = useState<boolean>(false)

	const prevPropValue = usePrevious(propValue)

	useEffect(() => {
		if (propValue !== prevPropValue && propValue !== localValue) {
			setLocalValue(propValue)
			if (propValue != null && propValue < 30) {
				setMode("select")
			}
		}
	}, [localValue, propValue, prevPropValue])

	const handleSelectChange = (newValue?: SelectChangeValue) => {
		if (newValue === "0" && onChooseZero) {
			onChooseZero()
		} else if (newValue === "30+") {
			setMode("input")
			setShowUpdateButton(true)
			setLocalValue(30)
			nextThread(() => onChange(30))
		} else {
			const numericValue = Number(newValue ?? 0)
			setLocalValue(numericValue)
			nextThread(() => onChange(numericValue))
		}
	}

	const handleInputChange = (newValue?: NumericChangeValue) => {
		if (useUpdateButton) {
			setLocalValue(newValue)
		} else {
			const numericValue = newValue ?? 0
			setLocalValue(numericValue)
			nextThread(() => onChange(numericValue))
		}
	}

	const onSaveInput = () => {
		if (!localValue && onChooseZero) {
			onChooseZero()
			return
		}
		onChange(Number(localValue ?? 0))
		setShowUpdateButton(false)
		if (localValue && localValue < 30) {
			setMode("select")
		}
	}

	const handleKeyDown = (event: QuantityKeyboardEvent) => {
		if (event.key === "Enter") {
			const target = event.target as HTMLInputElement | HTMLSelectElement
			const selectedValue = target.value?.trim()
			if (!selectedValue) return

			if (mode === "select") {
				handleSelectChange(selectedValue)
			} else {
				const numericValue = Number(selectedValue)
				if (!isNaN(numericValue)) {
					handleInputChange(numericValue)
				}
			}
			onSaveInput()
		}
	}

	const isUnitAtTop = () =>
		unitAtTop ?
			<div css={quantityControlLabel}>
				<label htmlFor="quantity-select" css={quantityLabel}>
					Qty
				</label>
				<Paragraph bold>{unitOfMeasure}</Paragraph>
			</div>
		:	<div css={quantityControlLabel}>
				<label htmlFor="quantity-select" css={quantityLabel}>
					Quantity
				</label>
			</div>

	return (
		<div>
			{isUnitAtTop()}

			<Box css={box}>
				{mode === "select" ?
					<>
						<div>
							<SelectEcomm
								options={options}
								value={String(localValue)}
								onChange={handleSelectChange}
								showNoSelectionOption={false}
								css={{ ...inputStyle, ...selectCss }}
								iconCss={iconCss}
								onKeyDown={handleKeyDown}
								mb={0}
								selectId="quantity-select"
							/>
						</div>
						{unitOfMeasure && !unitAtTop && (
							<Paragraph css={unitStyle}>{UnitMapping(unitOfMeasure)}</Paragraph>
						)}
					</>
				:	<Box css={box}>
						<div css={editBox}>
							<NumericInputEcomm
								css={inputStyle}
								value={localValue}
								onChange={handleInputChange}
								onFocus={() => setShowUpdateButton(true)}
								onBlur={onSaveInput}
								min={0}
								mb={0}
								onKeyDown={handleKeyDown}
								decimalScale={0}
							/>
							<AnimatedEntrance
								show={useUpdateButton && showUpdateButton}
								css={saveIcon}
							>
								<Button icon="check" onClick={onSaveInput} />
							</AnimatedEntrance>
						</div>
						{unitOfMeasure && isUnitAtTop()}
					</Box>
				}
				<div>
					{!!onChooseZero &&
						(clearVariant === "icon" ?
							<Box css={trashCan(unitOfMeasure)}>
								<IconButton
									icon="trash"
									onClick={onChooseZero}
									ariaLabel="remove product"
								/>
							</Box>
						:	<Button variant="text" onClick={onChooseZero} css={clearText}>
								Clear
							</Button>)}
				</div>
			</Box>
		</div>
	)
}
